// A review thread sent to a connected agent (components/RelayThread.svelte,
// the composer's Save & send): a range task (api/relayTask.ts) on the lines
// the thread is on, as the person sees them, with the thread's comments.
//
// The lines are named by content: the side (`old` before the change, `new`
// after it), git's object ID of that side's file (what `git rev-parse
// <commit>:<path>` names, so the agent can check it), and the commit it is
// read at. The Files view's marks name a blob by `blobId` of the decoded
// text (review/anchors.ts), which is the same ID except where decoding
// changed the bytes (a byte-order mark, invalid UTF-8). The new side is read at the head (an outdated thread: at the
// commit its comments were written on); the old side at the merge base of
// the base branch and that commit, as the Files view diffs a pull request.
// A file-level thread is on every line of the file.
import { blob, graphql, rest } from '../api/gh';
import { blobShasQuery, type ReviewThread } from '../api/queries';
import { pullKey } from '../api/relaySubject';
import { RANGE_TASK, type RangePayload, type TaskBody } from '../api/relayTask';

/** What a thread is sent with unless the person writes another request. */
export const THREAD_REQUEST = 'Address this review feedback and describe your changes and verification.';

/** The pull request a thread is on, as the page has it. */
export interface ThreadPr {
  owner: string;
  repo: string;
  number: number;
  headRefOid: string | null;
  baseRefOid: string | null;
}
type Thread = Pick<ReviewThread, 'id' | 'path' | 'line' | 'startLine' | 'originalLine' | 'originalStartLine' | 'diffSide' | 'startDiffSide' | 'subjectType' | 'comments'>;

/** Where a range task's lines are. */
export type ThreadRange = Pick<RangePayload, 'path' | 'side' | 'blob' | 'start_line' | 'end_line' | 'commit'>;

/** What a thread's range is read with: the merge base, the blob a path has at a commit, and its text (for a file-level thread's line count). The page's GitHub client, by default. */
export interface RangeReads {
  mergeBase: (owner: string, repo: string, base: string, tip: string) => Promise<string>;
  blobAt: (owner: string, repo: string, commit: string, path: string) => Promise<string | null>;
  text: (owner: string, repo: string, oid: string) => Promise<string | null>;
}
export const GITHUB_READS: RangeReads = {
  mergeBase: async (owner, repo, base, tip) => {
    const c = await rest<{ merge_base_commit?: { sha?: unknown } }>(`repos/${owner}/${repo}/compare/${base}...${tip}?per_page=1`);
    if (typeof c?.merge_base_commit?.sha !== 'string') throw new Error('GitHub did not say where this pull request branches from.');
    return c.merge_base_commit.sha;
  },
  blobAt: async (owner, repo, commit, path) => {
    const res = await graphql<{ repository: Record<string, { oid?: string } | null> | null }>(blobShasQuery(1), { owner, repo, e0: `${commit}:${path}` });
    return res.repository?.f0?.oid ?? null;
  },
  text: async (owner, repo, oid) => (await blob(owner, repo, oid)).text,
};

/** How many lines `text` has, as the diff counts them (a final newline ends the last line). */
const lineCount = (text: string) => (text === '' ? 0 : (text.endsWith('\n') ? text.slice(0, -1) : text).split('\n').length);

/**
 * Where `thread` is: its side, the blob of that side's text and the commit
 * it is read at, and its first and last line there (both on the thread's
 * side: a range that starts on the other side is cut to its own). Throws
 * when the pull request isn't loaded, or the file can't be read there.
 */
export async function threadRange(pr: ThreadPr, thread: Thread, reads: RangeReads = GITHUB_READS): Promise<ThreadRange> {
  const head = pr.headRefOid, base = pr.baseRefOid;
  if (!head || !base) throw new Error('This pull request has not loaded yet. Try again in a moment.');
  const side = thread.diffSide === 'LEFT' ? 'old' : 'new';
  const current = thread.line !== null;
  // An outdated thread is on the commit its comments were written on.
  const tip = current ? head : (thread.comments.nodes.find((c) => c.originalCommit)?.originalCommit?.oid ?? head);
  const commit = side === 'new' ? tip : await reads.mergeBase(pr.owner, pr.repo, base, tip);
  const oid = await reads.blobAt(pr.owner, pr.repo, commit, thread.path);
  if (oid === null) throw new Error(`Changeling could not read ${thread.path} at ${commit.slice(0, 7)}, where this thread is.`);
  const end = (current ? thread.line : thread.originalLine) ?? null;
  const start = (current ? thread.startLine : thread.originalStartLine) ?? null;
  const sameSide = !thread.startDiffSide || thread.startDiffSide === thread.diffSide;
  let first: number, last: number;
  if (thread.subjectType === 'FILE' || end === null) {
    // A file-level thread (or one GitHub gives no line) is on the whole file.
    const text = await reads.text(pr.owner, pr.repo, oid);
    if (text === null) throw new Error(`Changeling could not read ${thread.path} at ${commit.slice(0, 7)}, where this thread is.`);
    [first, last] = [1, Math.max(1, lineCount(text))];
  } else [first, last] = [start !== null && sameSide ? Math.min(start, end) : end, end];
  return { path: thread.path, side, blob: oid, start_line: first, end_line: last, commit };
}

/** What sending `thread` of pull request `pr` with `instruction` carries: where it is, its comments, and the request. */
export async function threadTask(pr: ThreadPr, thread: Thread, instruction: string, reads: RangeReads = GITHUB_READS): Promise<TaskBody> {
  const range = await threadRange(pr, thread, reads);
  return {
    kind: RANGE_TASK,
    payload: {
      subject: pullKey(pr.owner, pr.repo, pr.number),
      head: pr.headRefOid!,
      ...range,
      thread: { id: thread.id, comments: thread.comments.nodes.map((c) => ({ author: c.author?.login ?? null, body: c.body })) },
      instruction,
    },
  };
}
